/**
 * Fixture human identities (U-08-f) — the demo path's stand-in for the R1 session doors.
 *
 * Fixture mode never contacts a server, so it has to answer `POST /ops/v1/sessions`,
 * `DELETE /ops/v1/sessions/current` and `GET /ops/v1/permissions/effective` itself: without these
 * three answers the login screen and the identity badge would be unrenderable in the demo, and the
 * 370+ vitest tests that mount the console through fixture data would have no signed-in state to
 * exercise.
 *
 * The `capabilities` maps below are **not invented**: each is one row of
 * `acs_service/identity.py::_DEFAULT_LEVELS` (owner, admin, operator, user) with the `none` cells
 * dropped, because that is what the server publishes — `EffectivePermissions` says so in its own
 * docstring: only the capabilities the role holds appear, and everything else is absent.
 * `web/src/test/navigationCapabilities.test.ts` re-reads that Python table and fails if a row here
 * drifts from it, or if a navigation entry names a key the registry does not publish, so this stays
 * a copy of the server's answer rather than a second policy that can diverge. (Before U-08-f part 2
 * this file pointed at `api/session.test.ts` for that check; no such check lived there — the claim
 * was the bug, and the guard is where the promise is now kept.)
 *
 * The four roles are all here, so a test can ask for any one of them and assert the access state a
 * page renders for it — `fixtureEffectiveFor(role)` is the seam `test/render.tsx` uses, and it is
 * what makes «the UI mirrors the server's answer» a falsifiable sentence rather than an assertion.
 *
 * The password is demo data with no server behind it: fixture mode sends nothing anywhere, so
 * there is no credential to protect — and it is a fixture *username*, not a person. It exists so
 * the login form can be driven end-to-end in the demo and so a wrong password can be shown as the
 * 401 the real façade answers.
 */

import type { CapabilityWire, EffectivePermissions } from '../api/types';

/** One demo account: what it is called, what it holds, and what it may do. */
export interface FixtureConsoleUser {
  username: string;
  password: string;
  role: FixtureConsoleRole;
  capabilities: Record<string, CapabilityWire>;
}

/** The four roles §۲ of the RBAC doc publishes, all four demoed. */
export type FixtureConsoleRole = 'owner' | 'admin' | 'operator' | 'user';

/** The demo password, named once so a test does not have to guess it. */
export const FIXTURE_CONSOLE_PASSWORD = 'acs-demo';

/**
 * `expires_at` for a fixture session: a fixed far-future instant rather than a clock-derived one.
 * A real session token is short-lived and the server is the only thing that can say when it stops
 * working; a fixture credential has no expiry to honour, and pretending it did would make the
 * badge render a countdown to nothing.
 */
export const FIXTURE_SESSION_EXPIRES_AT = '2099-01-01T00:00:00Z';

export const FIXTURE_CONSOLE_USERS: readonly FixtureConsoleUser[] = [
  {
    username: 'owner-demo',
    password: FIXTURE_CONSOLE_PASSWORD,
    role: 'owner',
    capabilities: {
      'activation.read': 'edit',
      'activation.run': 'edit',
      'admin.backups.verify': 'edit',
      'admin.migrations.apply': 'edit',
      'admin.retention.run': 'edit',
      'alarm.ack': 'edit',
      'incident.manage': 'edit',
      'notify.manage': 'edit',
      'alarm.read': 'edit',
      'campaign.read': 'edit',
      'campaign.write': 'edit',
      'device.discover': 'edit',
      'device.params.raw': 'edit',
      'device.params.read': 'edit',
      'device.params.set': 'edit',
      'device.replace': 'edit',
      'diagnostics.run': 'edit',
      'events.read': 'edit',
      'firmware.read': 'edit',
      'firmware.write': 'edit',
      'fleet.audit.read': 'edit',
      'fleet.devices.bind': 'edit',
      'fleet.devices.read': 'edit',
      'fleet.tags.apply': 'edit',
      'health.read': 'edit',
      'jobs.cancel': 'edit',
      'jobs.read': 'edit',
      'keys.read': 'edit',
      'ops.outbox.read': 'edit',
      'ops.outbox.redrive': 'edit',
      'optical.read': 'edit',
      'plant.read': 'edit',
      'policy.matrix': 'edit',
      'profile.activate': 'edit',
      'profile.read': 'edit',
      'reconciliation.read': 'edit',
      'service.profile': 'edit',
      'settings.read': 'edit',
      'settings.thresholds': 'edit',
      'subscriber.manage': 'edit',
      'subscriber.read': 'edit',
      'templates.read': 'edit',
      'user.manage': 'edit',
    },
  },
  {
    username: 'operator-demo',
    password: FIXTURE_CONSOLE_PASSWORD,
    role: 'operator',
    capabilities: {
      'activation.read': 'read',
      'activation.run': 'edit',
      'alarm.ack': 'edit',
      'incident.manage': 'edit',
      'notify.manage': 'edit',
      'alarm.read': 'read',
      'campaign.read': 'read',
      'device.discover': 'read',
      'device.params.read': 'read',
      'device.params.set': 'edit',
      'diagnostics.run': 'edit',
      'events.read': 'read',
      'firmware.read': 'read',
      'fleet.devices.read': 'read',
      'fleet.tags.apply': 'read',
      'health.read': 'read',
      'jobs.cancel': 'edit',
      'jobs.read': 'read',
      'ops.outbox.read': 'read',
      'ops.outbox.redrive': 'edit',
      'optical.read': 'read',
      'plant.read': 'read',
      'profile.read': 'read',
      'reconciliation.read': 'read',
      'service.profile': 'edit',
      'settings.read': 'read',
      'subscriber.manage': 'edit',
      'subscriber.read': 'read',
    },
  },
  {
    // §۲'s default-deny role: six reads, no writes, and therefore the account that shows a
    // `DeniedState` without the console holding a rule about it. The sixth is `subscriber.read`
    // (S1) — the subscriber door masks the identifying fields for a READ-level holder, so a
    // `user` sees the rows without seeing anybody's phone number.
    username: 'user-demo',
    password: FIXTURE_CONSOLE_PASSWORD,
    role: 'user',
    capabilities: {
      'alarm.read': 'read',
      'device.params.read': 'read',
      'fleet.devices.read': 'read',
      'health.read': 'read',
      'jobs.read': 'read',
      'subscriber.read': 'read',
    },
  },
  {
    // The §۲ role between operator and owner: every family read, writes except the five
    // owner-only keys (`policy.matrix`, `user.manage`, `keys.read`, the two admin doors,
    // `profile.activate`). It exists so a test can render a `read` level for a key an operator
    // holds at `edit` — `fleet.tags.apply` and `ops.outbox.redrive` are two of them.
    username: 'admin-demo',
    password: FIXTURE_CONSOLE_PASSWORD,
    role: 'admin',
    capabilities: {
      'activation.read': 'read',
      'activation.run': 'edit',
      'alarm.ack': 'edit',
      'incident.manage': 'edit',
      'notify.manage': 'edit',
      'alarm.read': 'read',
      'campaign.read': 'read',
      'campaign.write': 'edit',
      'device.discover': 'edit',
      'device.params.raw': 'edit',
      'device.params.read': 'read',
      'device.params.set': 'edit',
      'device.replace': 'edit',
      'diagnostics.run': 'edit',
      'events.read': 'read',
      'firmware.read': 'read',
      'firmware.write': 'edit',
      'fleet.audit.read': 'read',
      'fleet.devices.bind': 'edit',
      'fleet.devices.read': 'read',
      'fleet.tags.apply': 'edit',
      'health.read': 'read',
      'jobs.cancel': 'edit',
      'jobs.read': 'read',
      'ops.outbox.read': 'read',
      'ops.outbox.redrive': 'edit',
      'optical.read': 'read',
      'plant.read': 'read',
      'profile.read': 'read',
      'reconciliation.read': 'read',
      'service.profile': 'edit',
      'settings.read': 'read',
      'settings.thresholds': 'edit',
      'subscriber.manage': 'edit',
      'subscriber.read': 'edit',
      'templates.read': 'read',
    },
  },
];

/** The four roles §۲ publishes — the same list `EffectivePermissions.roles` carries. */
export const FIXTURE_RBAC_ROLES = ['owner', 'admin', 'operator', 'user'] as const;

/**
 * The `permissions/effective` answer the fixture publishes for one role.
 *
 * This is the stand-in the console mirrors: `kind: 'human'`, the caller's own role, the role
 * vocabulary the server publishes, and only the levels that role holds (§۴'s row, `none` cells
 * dropped). `api/session.ts` seeds it in fixture mode, and a test that wants to prove the UI is a
 * mirror asks for a second role — or builds its own `EffectivePermissions` and seeds that.
 */
export function fixtureEffectiveFor(role: FixtureConsoleRole): EffectivePermissions {
  const user = fixtureUserFor(role);
  return {
    kind: 'human',
    subject: user.username,
    role: user.role,
    capabilities: user.capabilities,
    roles: [...FIXTURE_RBAC_ROLES],
  };
}

/** The demo account of one role — the username a fixture login is driven with. */
export function fixtureUserFor(role: FixtureConsoleRole): FixtureConsoleUser {
  const user = FIXTURE_CONSOLE_USERS.find((candidate) => candidate.role === role);
  if (!user) {
    throw new Error(`no fixture account for role "${role}" — §۲'s four roles must all be demoable`);
  }
  return user;
}

/** The `sid` a fixture session gets: stable per username, so a logout key names it (§۶). */
export function fixtureSidFor(username: string): string {
  return `sid-fixture-${username.replace(/[^A-Za-z0-9._-]/g, '_')}`;
}

/** A fixture bearer: obviously not a JWT, because nothing here is signed. */
export function fixtureTokenFor(username: string): string {
  return `fixture-session-${fixtureSidFor(username)}`;
}
